import { useCallback, useEffect, useState } from "react";
import youtubeIcon from "../../shared/assets/colored/youtube.png";
import vkIcon from "../../shared/assets/colored/vk.png";
import { Pencil } from "lucide-react";
import { Group, Stack, Title, Text, ActionIcon } from "@mantine/core";
import { Profile } from "../../shared/AuthContext";
import { checkLoginVk, checkLoginYoutube, startLoginVk, startLoginYoutube, useFetch, Fetch } from "../../shared/api";

/** The identity heading for the left column: full name (phystech email lives in
 *  the contacts section now). */
export function UserMetadata({ profile }: { profile: Profile }) {
  return (
    <Title order={1} fz={30} lh={1.2} mb="xl">
      {profile.student_profile?.first_name && profile.student_profile?.last_name
        ? `${profile.student_profile.first_name} ${profile.student_profile.last_name}`
        : profile.display_name}
    </Title>
  );
}

function Contact({ iconSrc, label, onEdit }: { iconSrc: string; label: string; onEdit: () => void }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <img src={iconSrc} alt="" style={{ height: "auto", width: 20 }} />
      <Text c="dimmed" style={{ flex: 1, minWidth: 0 }} truncate>{label}</Text>
      <ActionIcon variant="subtle" color="gray" size="sm" onClick={onEdit} aria-label="Изменить">
        <Pencil size={14} />
      </ActionIcon>
    </Group>
  );
}

const NOT_AUTHORIZED = "Вы не авторизованы";
const LOADING = "Загрузка...";

function useLoginStatus(checkFn: (fetch: Fetch) => Promise<Date>): string {
  const { fetch } = useFetch();
  const [status, setStatus] = useState(LOADING);

  const check = useCallback(async () => {
    try {
      const expiry = await checkFn(fetch);
      const timeLeft = expiry.getTime() - Date.now();
      if (timeLeft <= 0) {
        setStatus(NOT_AUTHORIZED);
      } else {
        const minutes = Math.floor(timeLeft / (1000 * 60));
        const days = Math.floor(minutes / (60 * 24));
        setStatus(days > 0 ? `Истекает через ${days} дн.` : `Истекает через ${minutes} мин.`);
      }
    } catch {
      setStatus(NOT_AUTHORIZED);
    }
  }, [fetch, checkFn]);

  useEffect(() => { check(); }, [check]);

  return status;
}

/** Platform authorization status (YouTube / VK) — compact, for the right column. */
export function AuthStatus() {
  const { fetch } = useFetch();

  const vkStatus = useLoginStatus(useCallback(
    fetch => checkLoginVk({ fetch }).then(r => new Date(r.expires_at * 1000)),
    []
  ));
  const youtubeStatus = useLoginStatus(useCallback(
    fetch => checkLoginYoutube({ fetch }).then(r => r.expiry),
    []
  ));

  const handleVkEdit = useCallback(async () => {
    const { redirect_uri } = await startLoginVk({ fetch });
    window.location.href = redirect_uri;
  }, [fetch]);

  const handleYoutubeEdit = useCallback(async () => {
    const { redirect_uri } = await startLoginYoutube({ fetch });
    window.location.href = redirect_uri;
  }, [fetch]);

  return (
    <Stack gap="sm">
      <Text fw={600}>Авторизация</Text>
      <Contact iconSrc={youtubeIcon} label={youtubeStatus} onEdit={handleYoutubeEdit} />
      <Contact iconSrc={vkIcon} label={vkStatus} onEdit={handleVkEdit} />
    </Stack>
  );
}
